import React, { useEffect, useMemo, useState } from 'react';
import { List, useTable, CreateButton } from '@refinedev/antd';
import { useList } from '@refinedev/core';
import { Table, Select, Space, Tag, Button, Card, Skeleton, Row, Col, Statistic, Segmented, Input } from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UnorderedListOutlined,
  HourglassOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';
import { apiClient } from '../../lib/api-client';

interface Trip {
  id: number;
  tripType: 'INTERCITY_TAXI' | 'INTERREGION_TAXI' | 'POSTAGE' | 'CARGO' | 'YOL_YOLAKAY' | 'SHAHARLAR_ARO';
  status: string;
  pickupAddress: string;
  destinationAddress: string;
  finalPrice: number | null;
  priceEstimate: number;
  // Tariff + passengerCount are present on InterregionTaxiTrip rows;
  // null/undefined for the other trip types where the seat-label badge
  // doesn't apply.
  tariff?: 'ECONOM' | 'STANDARD' | 'COMFORT' | 'BUSINESS' | 'SEVEN_PLUS' | null;
  passengerCount?: number | null;
  createdAt: string;
  requestedAt: string;
  acceptedAt?: string | null;
  canceledAt?: string | null;
  cancelledAt?: string | null;
  client: {
    fullName: string;
    phone: string;
  };
  driver: {
    fullName: string;
    phone: string;
  } | null;
  pickupRegion: { nameUz: string } | null;
  destinationRegion: { nameUz: string } | null;
  pickupDistrict: { nameUz: string } | null;
  destinationDistrict: { nameUz: string } | null;
}

/// Per-tariff seat cap on Viloyatlar Aro trips. Mirrors mobile
/// ViloyatlarAroTariffExt.maxSeats: Ekonom 4, Standard / Komfort / Biznes 3,
/// Minivan no full-salon concept (returns null).
const seatCapForTariff = (tariff?: string | null): number | null => {
  switch (tariff) {
    case 'ECONOM':
      return 4;
    case 'STANDARD':
    case 'COMFORT':
    case 'BUSINESS':
      return 3;
    default:
      return null;
  }
};

/// Returns "{N} ta joy" / "To'liq salon" for INTERREGION_TAXI rows; null
/// for other trip types and for Minivan (which has no full-salon
/// concept). Stays in Uzbek to match the existing badge convention used
/// across the rest of the admin trip area.
const viloyatlarAroSeatLabel = (trip: Trip): string | null => {
  if (trip.tripType !== 'INTERREGION_TAXI') return null;
  const seats = trip.passengerCount ?? 0;
  if (seats <= 0) return null;
  const cap = seatCapForTariff(trip.tariff);
  if (cap != null && seats >= cap) return "To'liq salon";
  return `${seats} ta joy`;
};

const WAITING_STATUSES = ['REQUESTED', 'ACCEPTED', 'DRIVER_ARRIVED'];

const formatWaitingTime = (startAt: string, endAt?: string | null): string => {
  const end = endAt ? new Date(endAt).getTime() : Date.now();
  const diff = end - new Date(startAt).getTime();
  if (diff < 0) return '0s';
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}${t('time.day')} ${hours}${t('time.hour')} ${minutes}${t('time.minute')}`;
  if (hours > 0) return `${hours}${t('time.hour')} ${minutes}${t('time.minute')}`;
  if (minutes > 0) return `${minutes}${t('time.minute')} ${seconds}${t('time.second')}`;
  return `${seconds}${t('time.second')}`;
};

// Color thresholds for the waiting-time pill. Tuned to the typical SafarGo
// driver-assignment SLA: under 3 min is "fast", over 10 min flags as "slow".
const waitingColorFor = (startAt: string, endAt?: string | null): string => {
  const end = endAt ? new Date(endAt).getTime() : Date.now();
  const minutes = (end - new Date(startAt).getTime()) / 60000;
  if (minutes < 3) return 'success';
  if (minutes < 10) return 'warning';
  return 'error';
};

const WaitingTimer = React.memo(({ requestedAt }: { requestedAt: string }) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Tag icon={<ClockCircleOutlined />} color={waitingColorFor(requestedAt)}>
      {formatWaitingTime(requestedAt)}
    </Tag>
  );
});

WaitingTimer.displayName = 'WaitingTimer';

type TripDateRange = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'allTime';

// Resolve a Segmented value to ISO `fromDate` / `toDate` for the backend.
// `allTime` returns nothing so the query stops constraining by date.
const resolveRange = (range: TripDateRange): { fromDate?: string; toDate?: string } => {
  const now = new Date();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (range) {
    case 'yesterday': {
      const start = new Date(today);
      start.setDate(start.getDate() - 1);
      const end = new Date(today);
      end.setMilliseconds(-1);
      return { fromDate: start.toISOString(), toDate: end.toISOString() };
    }
    case 'thisWeek': {
      // ISO week starting Monday.
      const start = new Date(today);
      const day = (start.getDay() + 6) % 7;
      start.setDate(start.getDate() - day);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'thisMonth': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'thisYear': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { fromDate: start.toISOString(), toDate: now.toISOString() };
    }
    case 'allTime':
      return {};
    case 'today':
    default:
      return { fromDate: today.toISOString(), toDate: now.toISOString() };
  }
};

export const TripList = () => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [tripTypeFilter, setTripTypeFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [range, setRange] = useState<TripDateRange>('today');
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Tracked separately from `searchTerm` so typing in the drawer doesn't
  // refetch on every keystroke — the active value only updates on submit.
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [pickupRegionId, setPickupRegionId] = useState<number | undefined>();
  const [destinationRegionId, setDestinationRegionId] = useState<
    number | undefined
  >();
  const [regions, setRegions] = useState<{ id: number; nameUz: string }[]>([]);

  useEffect(() => {
    apiClient
      .get<{ id: number; nameUz: string }[]>('/admin/regions')
      .then((res) => {
        const arr = Array.isArray(res.data)
          ? res.data
          : (res.data as { data?: { id: number; nameUz: string }[] })?.data ||
            [];
        setRegions(arr);
      })
      .catch(() => setRegions([]));
  }, []);

  // Re-derive only when `range` changes so we don't churn refine's filter
  // diffing on every render.
  const dateFilters = useMemo(() => {
    const { fromDate, toDate } = resolveRange(range);
    const out: Array<{ field: string; operator: 'gte' | 'lte'; value: string }> = [];
    if (fromDate) out.push({ field: 'fromDate', operator: 'gte', value: fromDate });
    if (toDate) out.push({ field: 'toDate', operator: 'lte', value: toDate });
    return out;
  }, [range]);

  const { tableProps } = useTable<Trip>({
    resource: 'admin/trips',
    syncWithLocation: true,
    pagination: {
      pageSize: 20,
    },
    filters: {
      permanent: [
        ...(tripTypeFilter ? [{ field: 'tripType', operator: 'eq' as const, value: tripTypeFilter }] : []),
        ...(statusFilter ? [{ field: 'statusGroup', operator: 'eq' as const, value: statusFilter }] : []),
        ...(activeSearch ? [{ field: 'search', operator: 'contains' as const, value: activeSearch }] : []),
        ...(pickupRegionId ? [{ field: 'pickupRegionId', operator: 'eq' as const, value: pickupRegionId }] : []),
        ...(destinationRegionId ? [{ field: 'destinationRegionId', operator: 'eq' as const, value: destinationRegionId }] : []),
        ...dateFilters,
      ],
    },
    sorters: {
      initial: [{ field: 'requestedAt', order: 'desc' }],
    },
  });

  const countQuery = (statusGroup?: string) => ({
    resource: 'admin/trips',
    pagination: { current: 1, pageSize: 1 },
    filters: [
      ...(statusGroup ? [{ field: 'statusGroup', operator: 'eq' as const, value: statusGroup }] : []),
      ...(activeSearch ? [{ field: 'search', operator: 'contains' as const, value: activeSearch }] : []),
      ...(pickupRegionId ? [{ field: 'pickupRegionId', operator: 'eq' as const, value: pickupRegionId }] : []),
      ...(destinationRegionId ? [{ field: 'destinationRegionId', operator: 'eq' as const, value: destinationRegionId }] : []),
      ...dateFilters,
    ],
  });

  // Count chips use the same 4 status buckets as the filter dropdown + row tags.
  const { data: totalData, isLoading: isTotalLoading } = useList<Trip>(countQuery());
  const { data: waitingData, isLoading: isWaitingLoading } = useList<Trip>(countQuery('WAITING'));
  const { data: activeData, isLoading: isActiveLoading } = useList<Trip>(countQuery('ACTIVE'));
  const { data: completedData, isLoading: isCompletedLoading } = useList<Trip>(countQuery('COMPLETED'));
  const { data: cancelledData, isLoading: isCancelledLoading } = useList<Trip>(countQuery('CANCELLED'));

  const totalCount = totalData?.total ?? 0;
  const waitingCount = waitingData?.total ?? 0;
  const activeCount = activeData?.total ?? 0;
  const completedCount = completedData?.total ?? 0;
  const cancelledCount = cancelledData?.total ?? 0;

  const tripTypeLabels: Record<string, string> = {
    INTERCITY_TAXI: t('tripType.intercityTaxi'),
    INTERREGION_TAXI: t('tripType.interregionTaxi'),
    SHAHARLAR_ARO: t('tripType.shaharlarAro'),
    POSTAGE: t('tripType.postage'),
    CARGO: t('tripType.cargo'),
    YOL_YOLAKAY: t('tripType.yolYolakay'),
  };

  const tripTypeColors: Record<string, string> = {
    INTERCITY_TAXI: 'blue',
    INTERREGION_TAXI: 'purple',
    SHAHARLAR_ARO: 'magenta',
    POSTAGE: 'orange',
    CARGO: 'cyan',
    YOL_YOLAKAY: 'green',
  };

  const statusLabels: Record<string, string> = {
    REQUESTED: t('status.requested'),
    ACCEPTED: t('status.accepted'),
    DRIVER_ARRIVED: t('status.driverArrived'),
    PICKED_UP: t('status.pickedUp'),
    ON_THE_WAY: t('status.onTheWay'),
    IN_PROGRESS: t('status.inProgress'),
    COMPLETED: t('status.completed'),
    CANCELLED: t('status.cancelled'),
    INTERRUPTED: t('status.interrupted'),
    NO_SHOW: t('status.noShow'),
    // Yol-Yolakay statuses
    ACTIVE: t('status.active'),
    MATCHED: t('status.matched'),
    EXPIRED: t('status.expired'),
  };

  // Four coarse status buckets. The filter dropdown, the count chips, AND each
  // row's status tag all use these — so the list reads consistently no matter
  // the trip type (taxi vs Yol-Yolakay/Shaharlar, which use different raw
  // statuses for the same lifecycle stage). The raw status is kept as a tooltip
  // on the row tag so granular detail isn't lost.
  const STATUS_BUCKETS = [
    { value: 'WAITING', label: t('status.bucketWaiting'), color: 'orange' },
    { value: 'ACTIVE', label: t('status.bucketActive'), color: 'processing' },
    { value: 'COMPLETED', label: t('status.bucketCompleted'), color: 'green' },
    { value: 'CANCELLED', label: t('status.bucketCancelled'), color: 'red' },
  ];
  const statusToBucket = (status: string): string => {
    if (['REQUESTED', 'ACTIVE', 'PENDING'].includes(status)) return 'WAITING';
    if (status === 'COMPLETED') return 'COMPLETED';
    if (['CANCELLED', 'INTERRUPTED', 'NO_SHOW', 'EXPIRED'].includes(status)) return 'CANCELLED';
    // ACCEPTED, MATCHED, CONFIRMED, DRIVER_ARRIVED, PICKED_UP, ON_THE_WAY, IN_PROGRESS, …
    return 'ACTIVE';
  };
  const bucketMeta = (bucket: string) =>
    STATUS_BUCKETS.find((b) => b.value === bucket) || { label: bucket, color: 'default' };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' ' + t('common.sum');
  };

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('table.tripType'),
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string, record: Trip) => {
        const seatLabel = viloyatlarAroSeatLabel(record);
        return (
          <Space size={4} wrap>
            <Tag color={tripTypeColors[type]}>{tripTypeLabels[type] || type}</Tag>
            {seatLabel && (
              <Tag color="cyan" style={{ marginRight: 0 }}>
                {seatLabel}
              </Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        // Show the coarse bucket (consistent across all trip types); keep the
        // exact raw status on hover so detail is still available.
        const b = bucketMeta(statusToBucket(status));
        return (
          <Tag color={b.color} title={statusLabels[status] || status}>
            {b.label}
          </Tag>
        );
      },
    },
    {
      title: t('table.waitingTime'),
      key: 'waitingTime',
      render: (_: unknown, record: Trip) => {
        const requestedAt = record.requestedAt || record.createdAt;
        if (WAITING_STATUSES.includes(record.status)) {
          return <WaitingTimer requestedAt={requestedAt} />;
        }
        // For terminal/intermediate states, freeze the timer at the moment the
        // wait actually ended: driver accepted, or trip cancelled.
        const endAt =
          record.acceptedAt ||
          record.canceledAt ||
          record.cancelledAt ||
          null;
        return (
          <Tag icon={<ClockCircleOutlined />} color={waitingColorFor(requestedAt, endAt)}>
            {formatWaitingTime(requestedAt, endAt)}
          </Tag>
        );
      },
    },
    {
      title: t('table.route'),
      key: 'route',
      width: 200,
      render: (_: unknown, record: any) => {
        const pickup = [
          record.pickupRegion?.nameUz,
          record.pickupDistrict?.nameUz,
        ].filter(Boolean).join(', ');
        const dest = [
          record.destinationRegion?.nameUz,
          record.destinationDistrict?.nameUz,
        ].filter(Boolean).join(', ');
        const pickupText = pickup || record.pickupAddress || '-';
        const destText = dest || record.destinationAddress || '-';
        const destCount = record.destinations?.length;
        return (
          <div style={{ fontSize: 12, maxWidth: 180 }}>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={pickupText}>
              <EnvironmentOutlined style={{ color: '#52c41a' }} /> {pickupText}
            </div>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={destText}>
              <EnvironmentOutlined style={{ color: '#f5222d' }} /> {destText}
            </div>
            {destCount > 1 && (
              <div style={{ color: '#7F8C8D', marginTop: 2 }}>+{destCount - 1} {t('trips.moreDestinations')}</div>
            )}
          </div>
        );
      },
    },
    {
      title: t('table.client'),
      dataIndex: 'client',
      key: 'client',
      render: (client: Trip['client']) => (
        <div>
          <div>{client?.fullName || t('common.noName')}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{client?.phone}</div>
        </div>
      ),
    },
    {
      title: t('table.driver'),
      dataIndex: 'driver',
      key: 'driver',
      render: (driver: Trip['driver']) =>
        driver ? (
          <div>
            <div>{driver.fullName || t('common.noName')}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{driver.phone}</div>
          </div>
        ) : (
          <span style={{ color: '#7F8C8D' }}>-</span>
        ),
    },
    {
      title: t('table.price'),
      dataIndex: 'finalPrice',
      key: 'finalPrice',
      render: (price: number | null, record: Trip) => {
        const perSeat =
          price != null && price > 0 ? price : record.priceEstimate;
        if (perSeat == null || perSeat <= 0) return '-';
        // Viloyatlar Aro (INTERREGION_TAXI), Yol-Yolakay and Shaharlar
        // Aro store prices PER SEAT — show total as "{total}
        // ({perSeat} × {N})" so admins can see both at a glance. The
        // postage / cargo / intercity columns are already trip-totals.
        const seats = record.passengerCount || 1;
        const PER_SEAT_TYPES: Trip['tripType'][] = [
          'INTERREGION_TAXI',
          'YOL_YOLAKAY',
          'SHAHARLAR_ARO',
        ];
        const isPerSeat = PER_SEAT_TYPES.includes(record.tripType) && seats > 1;
        if (!isPerSeat) return <strong>{formatCurrency(perSeat)}</strong>;
        const total = perSeat * seats;
        return (
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600 }}>{formatCurrency(total)}</div>
            <div style={{ color: '#7F8C8D', fontSize: 11 }}>
              {formatCurrency(perSeat)} × {seats}
            </div>
          </div>
        );
      },
    },
    {
      title: t('table.date'),
      key: 'requestedAt',
      render: (_: unknown, record: Trip) => {
        const date = record.requestedAt || record.createdAt;
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        return (
          <div>
            <div>{day}.{month}.{year}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{hours}:{mins}</div>
          </div>
        );
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: Trip) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/trips/${record.tripType}/${record.id}`)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/trips/${record.tripType}/${record.id}/edit`)}
          />
        </Space>
      ),
    },
  ];

  return (
    <List
      title={t('trips.title')}
      headerButtons={
        <Space wrap>
          <Segmented<TripDateRange>
            value={range}
            onChange={(val) => setRange(val as TripDateRange)}
            options={[
              { label: t('dashboard.range.today'), value: 'today' },
              { label: t('dashboard.range.yesterday'), value: 'yesterday' },
              { label: t('dashboard.range.thisWeek'), value: 'thisWeek' },
              { label: t('dashboard.range.thisMonth'), value: 'thisMonth' },
              { label: t('dashboard.range.thisYear'), value: 'thisYear' },
              { label: t('dashboard.range.allTime'), value: 'allTime' },
            ]}
          />
          <Select
            placeholder={t('common.type')}
            allowClear
            style={{ width: 140 }}
            value={tripTypeFilter}
            onChange={(value) => setTripTypeFilter(value)}
            options={Object.entries(tripTypeLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            placeholder={t('common.status')}
            allowClear
            style={{ width: 170 }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            options={STATUS_BUCKETS.map(({ value, label }) => ({ value, label }))}
          />
          <Select
            showSearch
            allowClear
            placeholder="Qayerdan (viloyat)"
            style={{ width: 180 }}
            value={pickupRegionId}
            onChange={(v) => setPickupRegionId(v)}
            optionFilterProp="label"
            options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
          />
          <Select
            showSearch
            allowClear
            placeholder="Qayerga (viloyat)"
            style={{ width: 180 }}
            value={destinationRegionId}
            onChange={(v) => setDestinationRegionId(v)}
            optionFilterProp="label"
            options={regions.map((r) => ({ value: r.id, label: r.nameUz }))}
          />
          {/* Phone / name search lives on the page (not in the filter
              drawer) so it's discoverable at a glance. Backend matches via
              Prisma `contains`, which works on phone substrings — e.g.
              typing the last 4 digits of a number ("1234") finds
              "998901234567". */}
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            placeholder="Telefon yoki ism (oxirgi 4 raqam ham yetadi)"
            value={searchTerm}
            onChange={(e) => {
              const v = e.target.value;
              setSearchTerm(v);
              // Clearing via the X icon should clear the active filter
              // immediately; typing waits for Enter so requests don't fire
              // on every keystroke.
              if (v === '') setActiveSearch('');
            }}
            onPressEnter={() => setActiveSearch(searchTerm.trim())}
            style={{ width: 320 }}
          />
          {can('trips', 'create') && <CreateButton>{t('trips.newTrip')}</CreateButton>}
        </Space>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title="Jami"
              value={totalCount}
              loading={isTotalLoading}
              prefix={<UnorderedListOutlined style={{ color: '#595959' }} />}
              valueStyle={{ color: '#262626' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={bucketMeta('WAITING').label}
              value={waitingCount}
              loading={isWaitingLoading}
              prefix={<HourglassOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={bucketMeta('ACTIVE').label}
              value={activeCount}
              loading={isActiveLoading}
              prefix={<SyncOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={statusLabels.COMPLETED}
              value={completedCount}
              loading={isCompletedLoading}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={8} lg={4}>
          <Card>
            <Statistic
              title={statusLabels.CANCELLED}
              value={cancelledCount}
              loading={isCancelledLoading}
              prefix={<StopOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <Table
        {...tableProps}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1200 }}
        loading={
          tableProps.loading
            ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 8 }} /></Card> }
            : false
        }
      />

    </List>
  );
};
